import * as React from 'react';
import { useState } from 'react';

import { BazaarRatingSummary, BazaarVoiceReviews } from '@phx-husky/bazaarvoice-components';
import { useBazaarVoice } from '@phx-husky/use-bazaarvoice';
import Slider from 'react-slick';

import PageList from './blocks/pageList';
import Accordion from './blocks/Accordion';
import ProductPageSetTile from './blocks/productPageSetTile';
import { wrapText, getHardcodedPathByLocaleCode } from '../utils';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const featuredImagesSettings = {
  dots: false,
  arrows: true,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
};

const ProductPageLayout = ({
  product,
  siteSettings: { product_set_page, bazaarvoice_url },
  currentLocaleCode,
}) => {
  const [activeVariantIndex, setActiveVariantIndex] = useState(null);
  const handleVariantClick = (index) => {
    setActiveVariantIndex(index);
  };

  // 'https://apps.bazaarvoice.com/deployments/airwick/main_site/production/en_US/bv.js'
  useBazaarVoice(bazaarvoice_url);
  // useBazaarVoice(
  //   "https://display.ugc.bazaarvoice.com/static/airwick/main_site/129/19856/en_US/scripts/secondary.js"
  // );
  const isBrowser = typeof window !== 'undefined';
  const [isMobile, setIsMobile] = React.useState(isBrowser ? window.innerWidth < 768 : false);

  React.useEffect(() => {
    function handleWindowResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const settings = {
    speed: 500,
    slidesToShow: isMobile ? 2.2 : 5,
    slidesToScroll: 2,
    infinite: false,
    arrows: !isMobile,
    dotsClass: 'slick-dots slider-dots',
    appendDots: (dots) => <ul className="slider-dots slick-dots"> {dots} </ul>,
    dots: true,
  };

  return (
    <>
      <div className="w-full grid grid-cols-12">
        <div className="col-span-12 grid grid-cols-12">
          <div className="col-span-12 md:col-span-6">
            <div className="product-image">
              <div className="product-image-slider">
                <Slider {...featuredImagesSettings}>
                  {product.images.map((image, index) => (
                    <div key={index}>
                      <img src={image.url} alt={image.description} />
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-6">
            <div className="product-description flex flex-col">
              <h1 class="h2">{product.title}</h1>
              <article dangerouslySetInnerHTML={{ __html: wrapText(product.content) }} />
              <div>
                <p className='d-none-variants'>{product_set_page?.title_variants}</p>
                <ul className="variants d-none-variants">
                  {product.variants.map((variant, index) => {
                    return (
                      <li
                        key={index}
                        className={index === activeVariantIndex ? 'active' : ''}
                        onClick={() => handleVariantClick(index)}
                      >
                        <div className="variant-image">
                          <img src={variant.image.url} alt={variant.title} />
                        </div>
                        <h4>{variant.title}</h4>
                      </li>
                    );
                  })}
                </ul>
                <a
                  className="learn-more inline-flex flex-col"
                  href={getHardcodedPathByLocaleCode('allFragrances', currentLocaleCode)}
                >
                  <span>{product_set_page?.link_text_all_fragrances}</span>
                </a>
                <div className="flex flex-col sm:flex-row justify-start items-start sm:items-center space-x-2">
                  <div className="product-pricespiderbutton product-base-component">
                    <div className="product-pricespiderbutton">
                      <div
                        ps-sku={product.price_spider_id}
                        className="ps-widget ps-enabled"
                        ps-widget-type="lightbox"
                        tabIndex="0"
                        aria-disabled="false"
                        role="button"
                        aria-label={product_set_page?.button_aria_label_where_to_buy}
                      >
                        <span className="ps-button-label">
                          {product_set_page?.button_text_where_to_buy}
                        </span>
                      </div>
                    </div>
                  </div>
                  {product.bazaar_voice_id && (
                    <BazaarRatingSummary productId={product.bazaar_voice_id} seo={false} />
                  )}
                </div>
              </div>
              <div dangerouslySetInnerHTML={{ __html: product.long_description }} />
            </div>
          </div>
        </div>
        <div className="col-span-12 slider">
          <h2>{product_set_page?.title_related_products}</h2>

          <Slider {...settings}>
            {product.related_products.map((related_product) => {
              return (
                <ProductPageSetTile
                  key={related_product.title}
                  product={related_product}
                  linkTextCard={product_set_page?.link_text_card_related_products}
                />
              );
            })}
          </Slider>
        </div>
        <div className="col-span-12 grid grid-cols-12 instructions">
          {product.instructions.row.map((instruction) => {
            const isIframe = instruction.content_left.includes('<iframe');

            return (
              <>
                {instruction.content_left.length ? (
                  isIframe ? (
                    <div className="col-span-12 md:col-span-6 instruction iframe">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: instruction.content_left,
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      className="col-span-12 md:col-span-6 instruction"
                      dangerouslySetInnerHTML={{
                        __html: instruction.content_left,
                      }}
                    />
                  )
                ) : (
                  instruction.image && (
                    <div className="col-span-12 md:col-span-6 instruction-image">
                      <img src={instruction.image.url} alt={product.title} />
                    </div>
                  )
                )}
                {instruction.content_right.length ? (
                  <div
                    className="col-span-12 md:col-span-6 instruction"
                    dangerouslySetInnerHTML={{
                      __html: instruction.content_right,
                    }}
                  />
                ) : (
                  instruction.image && (
                    <div className="col-span-12 md:col-span-6 instruction-image">
                      <img src={instruction.image.url} alt={product.title} />
                    </div>
                  )
                )}
              </>
            );
          })}
        </div>
        <div className="col-span-12 grid grid-cols-12 safety">
          <div className="col-span-12 md:col-span-4 safety-title">
            <h2>{product_set_page?.title_safety_information}</h2>
          </div>
          <div className="col-span-12 md:col-span-8">
            <Accordion items={product.safety_informations_and_ingredients.tab} />
          </div>
        </div>
        <a href="#skip-rating" aria-label='Skip rating and reviews section' className='col-span-12 grid skip-ratings'>Skip rating and reviews</a>
        {product.bazaar_voice_id && (
          <div className="col-span-12 container reviews">
            <h2>{product_set_page?.title_customers_ratings}</h2>
            <BazaarVoiceReviews productId={product.bazaar_voice_id} seo={false} />
          </div>
        )}
        <div id="skip-rating"></div>
        {product.page_list && (
          <PageList
            data={{
              layout: 'List',
              title: product_set_page?.title_page_list,
              pages: product.page_list,
              url: product.url_more,
              urltext: product.urltext_more,
            }}
          />
        )}
      </div>
    </>
  );
};
export default ProductPageLayout;
